import { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import Spinner from '@/components/Spinner';
import { useShallowEqualSelector } from '@/hooks';
import { clearDocuments, setPage, setSearchTerm } from '@/store/myDocuments';
import { fetchDocuments } from '@/store/myDocuments/thunks';
import { showError } from '@/utils';

import Table from './DocumentTable/Table';
import EmptySection from './EmptySection';

function DocumentFilled({ section, folderId }) {
	const DEFAULT_PAGE = 1;
	const [searchParams, setSearchParams] = useSearchParams();
	const pageURL = parseInt(searchParams.get('page'), 10) || DEFAULT_PAGE;
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const { isCompleted, isEmpty, isLoading, page, searchTerm, transactions } =
		useShallowEqualSelector(({ myDocuments }) => ({
			isCompleted: myDocuments.fetchDocuments.completed,
			isEmpty: myDocuments.isEmpty,
			isLoading: myDocuments.fetchDocuments.loading,
			page: myDocuments.page,
			searchTerm: myDocuments.searchTerm,
			transactions: myDocuments.transactions
		}));

	useEffect(() => {
		if (pageURL) {
			dispatch(setPage(pageURL));
		}
	}, []);

	useEffect(() => {
		setSearchParams(page === DEFAULT_PAGE ? {} : { page });
		dispatch(fetchDocuments(folderId)).unwrap().catch(showError);
	}, [page, searchTerm]);

	useEffect(() => {
		return () => {
			dispatch(clearDocuments());
			dispatch(setSearchTerm(''));
		};
	}, []);

	return (
		<div className="relative flex w-full flex-col gap-2 md:gap-4">
			<h2 className="text-xs font-semibold leading-5">
				{t(`myDocuments.${section}.title`)}
			</h2>
			{isLoading && (
				<Spinner
					className="absolute inset-x-0 top-[96px] z-10 mx-auto text-black md:top-[104px]"
					size={40}
				/>
			)}
			{isEmpty && !searchTerm.length && (
				<EmptySection section={section} className="py-16" />
			)}
			{isCompleted && !isEmpty && (
				<div className="flex flex-col gap-3 md:gap-4">
					<Table documents={transactions} />
				</div>
			)}
		</div>
	);
}

export default DocumentFilled;
